import React, { Component } from "react";
import { Card, Row, Col, Button, Form, Spinner, Table } from "react-bootstrap";
import "./_offlineExams.scss";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import { components } from 'react-select';
import SelectDropDown from '../selectdropdown/SelectDropDown';
import getTemplate from './reportTemplates';
import { scopeBranches, onBranchScopeChange } from "../../../utils/branchScope";

const GET_BRANCHES = gql`
  query getBranches($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name     
      sections {
        id
        class_id
        section_name
      }
    }
  }
`;

const GET_REPORT_CARD_STUDENTS = gql`
  query (
    $class_id: Int!
    $stream_id: Int
    $academic_year_id: Int!
    $exam_phase_id: Int!
    $institute_id: Int
    $page: Int
    $limit: Int
  ) {
    getReportCardStudents(
      class_id: $class_id
      stream_id: $stream_id
      academic_year_id: $academic_year_id
      exam_phase_id: $exam_phase_id
      institute_id: $institute_id
      page: $page
      limit: $limit
    ) {
      total_students
      generated_count
      pending_count
      total_count
      page
      limit
      students {
        student_id
        reg_id
        admission_no
        student_name
        gender
        class
        academic_year
        exam_phase
        dob
        father_name
        father_phone
        mother_name
        total_earned
        total_max
        overall_percentage
        overall_grade
        grade_point
        rank_no
        status
        working_days
        days_present
        attendance_percentage
        subjects {
          subject_name
          subject_total
          subject_max
          subject_percentage
          subject_grade
          grade_point
          class_avg
          high_score
          internals {
            internal_name
            max_marks
            marks_obtained
          }
        }
      }
    }
  }
`;

const GET_MONTHLY_ATTENDANCE = gql`
  query getStudentAttendanceReportDetails(
    $institute_id: Int!
    $branch_id: Int!
    $class_id: String
    $section_id: String
    $from_date: String
    $to_date: String
  ) {
    getStudentAttendanceReportDetails(
      institute_id: $institute_id
      branch_id: $branch_id
      class_id: $class_id
      section_id: $section_id
      from_date: $from_date
      to_date: $to_date
    ) {
      id
      working_days
      present
      absent
    }
  }
`;

// ─── Shared CSS for the printed report ──────────────────────────────────────
const REPORT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,800;0,900;1,800;1,900&family=Roboto+Slab:wght@500;600;700&family=Inter+Tight:wght@400;500;600;700;800&display=swap');

  :root {
    --t-page:     #fbf8ed;
    --t-ink:      #2a2a2a;
    --t-border:   #3a6a4c;
    --t-line:     #dde8e0;
    --t-brand:    #3a6a4c;
    --t-brand2:   #5a8a6e;
    --t-accent:   #c89a2a;
    --t-panel-bg: #2a4a35;
    --t-panel-fg: #ffffff;
    --t-panel-hi: #f0d889;
    --t-band-bg:  #f7e5a5;
    --t-band-fg:  #2a4a35;
    --t-photo-bg: #ede5c8;
    --t-chart-1:  #3a6a4c;
    --t-chart-2:  #c89a2a;
    --t-chart-3:  #9bc0a7;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { background: #fff; font-family: "Inter Tight", system-ui, sans-serif; }

  .rc {
    width: 794px; height: 1123px; background: var(--t-page);
    color: var(--t-ink); font-family: "Inter Tight", system-ui, sans-serif;
    font-size: 11px; line-height: 1.35;
    padding: 16px 22px 14px; position: relative; overflow: hidden;
    display: flex; flex-direction: column;
    page-break-after: always;
  }
  .rc:last-child { page-break-after: auto; }

  .rc-stamp { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
    pointer-events: none; user-select: none; z-index: 0; opacity: 0.05; }
  .rc-stamp img { width: 820px; height: 1080px; object-fit: contain; display: block;
    transform: rotate(-14deg); filter: contrast(1.05); }

  .rc-inner { display: flex; flex-direction: column; flex: 1; gap: 8px; position: relative; z-index: 1; }

  /* HEADER */
  .rc-hdr { display: grid; grid-template-columns: 148px 1fr 250px; gap: 14px; align-items: center; padding: 4px 0; }
  .rc-crest { width: 144px; height: 144px; display: grid; place-items: center; position: relative; }
  .rc-crest img { width: 144px; height: 144px; object-fit: contain; display: block; filter: drop-shadow(0 2px 6px rgba(0,0,0,0.18)); }
  .rc-crest .fallback { position: absolute; inset: 0; display: grid; place-items: center;
    width: 144px; height: 144px; border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #fff 0%, #f0e8d2 100%);
    border: 3px solid var(--t-brand); font-family: "Playfair Display", Georgia, serif;
    font-weight: 900; font-style: italic; font-size: 70px; color: var(--t-brand);
    letter-spacing: -2px; box-shadow: 0 0 0 2px var(--t-accent) inset; }

  .rc-brand { display: flex; flex-direction: column; gap: 2px; padding-left: 4px; }
  .rc-brand .nm { font-family: "Playfair Display", Georgia, serif; font-weight: 900;
    font-style: italic; font-size: 58px; line-height: 0.9; letter-spacing: -1.5px;
    color: var(--t-brand); text-shadow: 1px 1px 0 rgba(0,0,0,0.06); }
  .rc-brand .tag { font-weight: 700; font-size: 12px; letter-spacing: 0.06em; color: var(--t-brand2); margin-top: 2px; }
  .rc-brand .accent-line { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
  .rc-brand .accent-line .l { flex: 1; height: 2px; background: linear-gradient(90deg, var(--t-brand) 0%, transparent 100%); }
  .rc-brand .accent-line .est { font-size: 12px; letter-spacing: 0.4em; color: var(--t-accent); font-weight: 700; line-height: 1; }

  .rc-panel { background: var(--t-panel-bg); color: var(--t-panel-fg); padding: 9px 12px 9px 14px;
    display: flex; flex-direction: column; gap: 1px; min-height: 96px; justify-content: center;
    border-left: 4px solid var(--t-accent); }
  .rc-panel .b { display: block; line-height: 1.15; padding: 1px 0; }
  .rc-panel .b .nm { display: block; font-size: 11px; font-weight: 800; color: var(--t-panel-hi); }
  .rc-panel .b .area { display: block; font-size: 8.5px; opacity: 0.88; }

  /* TITLE BAND */
  .rc-title { background: var(--t-band-bg); color: var(--t-band-fg); border: 1px solid var(--t-border);
    padding: 9px 18px; font-family: "Roboto Slab", serif; font-weight: 700; font-size: 16px;
    text-align: center; letter-spacing: 0.04em; }
  .rc-title .d { color: var(--t-accent); margin: 0 12px; font-size: 12px; }

  /* BIO */
  .rc-bio { display: grid; grid-template-columns: 1fr 250px 80px; border: 1px solid var(--t-border); }
  .rc-bio table { width: 100%; border-collapse: collapse; }
  .rc-bio table td { border-bottom: 1px solid var(--t-line); padding: 6px 12px; font-size: 11px; height: 27px; }
  .rc-bio table tr:last-child td { border-bottom: 0; }
  .rc-bio table td.lbl { width: 36%; font-weight: 700; color: var(--t-brand); font-size: 10.5px; }
  .rc-bio table + table { border-left: 1px solid var(--t-line); }
  .rc-bio .photo { background: var(--t-photo-bg); border-left: 1px solid var(--t-line);
    display: grid; place-items: center; font-size: 8.5px; opacity: 0.55; letter-spacing: 0.18em; text-transform: uppercase; }

  /* SUBJECTS */
  .rc-subj { width: 100%; border-collapse: collapse; border: 1px solid var(--t-border); }
  .rc-subj th, .rc-subj td { border-bottom: 1px solid var(--t-line); padding: 7px 10px; font-size: 11px; text-align: center; }
  .rc-subj th { background: var(--t-band-bg); color: var(--t-band-fg); font-family: "Roboto Slab", serif;
    font-weight: 700; letter-spacing: 0.02em; padding: 9px 10px; border-bottom: 1px solid var(--t-border); }
  .rc-subj td.name { text-align: left; font-weight: 700; padding-left: 16px; color: var(--t-brand); }
  .rc-subj td.grade { font-weight: 800; color: var(--t-accent); font-size: 12px; }
  .rc-subj td.num { font-weight: 600; }
  .rc-subj tbody tr:last-child td { border-bottom: 0; }
  .rc-subj tr.tot td { background: var(--t-brand); color: #fff; font-weight: 800; font-size: 12px;
    border-color: var(--t-brand); border-top: 1px solid var(--t-border); }
  .rc-subj tr.tot td.grade, .rc-subj tr.tot td.name { color: #fff; }
  .rc-subj tr.tot td.name { padding-left: 16px; font-family: "Roboto Slab", serif; letter-spacing: 0.04em; }
  .rc-cgpa { display: inline-block; background: var(--t-accent); color: var(--t-band-fg);
    padding: 3px 8px; font-weight: 800; font-size: 10.5px; border-radius: 2px; white-space: nowrap;
    box-shadow: inset 0 -1px 0 rgba(0,0,0,0.15); }

  /* LEGEND */
  .rc-legend { background: var(--t-band-bg); border: 1px solid var(--t-border); padding: 6px 14px;
    font-size: 10px; color: var(--t-band-fg); letter-spacing: 0.02em; }
  .rc-legend b { color: var(--t-brand); font-weight: 800; margin-right: 6px; font-family: "Roboto Slab", serif; }

  /* ATTENDANCE */
  .rc-att-h { text-align: center; font-family: "Roboto Slab", serif; font-weight: 700; font-size: 18px;
    color: var(--t-brand); letter-spacing: 0.04em; padding: 2px 0 0;
    display: flex; align-items: center; justify-content: center; gap: 12px; white-space: nowrap; }
  .rc-att-h .span { font-size: 12px; color: var(--t-ink); opacity: 0.75; font-weight: 500; font-family: "Inter Tight", sans-serif; letter-spacing: 0; }
  .rc-att-h::before, .rc-att-h::after { content: ""; flex: 1 1 0; max-width: 80px; min-width: 24px; height: 1px; background: var(--t-brand); opacity: 0.4; }
  .rc-att { width: 100%; border-collapse: collapse; border: 1px solid var(--t-border); }
  .rc-att th, .rc-att td { border-bottom: 1px solid var(--t-line); padding: 6px 8px; text-align: center; font-size: 11px; }
  .rc-att tbody tr:last-child td { border-bottom: 0; }
  .rc-att th { background: var(--t-band-bg); color: var(--t-band-fg); font-family: "Roboto Slab", serif; font-weight: 700; border-bottom: 1px solid var(--t-border); }
  .rc-att td.lbl { text-align: left; font-weight: 700; padding-left: 14px; width: 28%; color: var(--t-brand); }
  .rc-att .green { color: #276c3a; font-weight: 700; }
  .rc-att .red   { color: #b83232; font-weight: 700; }

  /* CHART + REMARKS */
  .rc-cr { display: grid; grid-template-columns: 1.5fr 1fr; gap: 10px; flex: 1; }
  .rc-cr > div { border: 1px solid var(--t-border); display: flex; flex-direction: column; }
  .rc-chart { padding: 8px 14px; flex: 1; display: flex; flex-direction: column; }
  .rc-chart-h { font-family: "Roboto Slab", serif; font-weight: 700; font-size: 12px;
    color: var(--t-brand); letter-spacing: 0.04em; padding: 0 0 6px;
    border-bottom: 1px solid var(--t-border); margin-bottom: 8px;
    display: flex; justify-content: space-between; align-items: center; }
  .rc-chart-h .scale { font-family: "Inter Tight", sans-serif; font-weight: 500; font-size: 8.5px; opacity: 0.6; letter-spacing: 0.1em; text-transform: uppercase; }
  .rc-chart-area { flex: 1; display: grid; grid-template-columns: 22px 1fr; gap: 4px; min-height: 80px; }
  .rc-chart-y { display: flex; flex-direction: column-reverse; justify-content: space-between;
    font-size: 8px; opacity: 0.6; text-align: right; padding: 2px 0; font-weight: 600; }
  .rc-chart-bars { position: relative; border-left: 1.5px solid var(--t-border); border-bottom: 1.5px solid var(--t-border);
    display: flex; align-items: flex-end; justify-content: space-around; padding: 0 2px; }
  .rc-chart-bars .grid { position: absolute; left: 0; right: 0; top: 0; bottom: 0;
    display: flex; flex-direction: column-reverse; justify-content: space-between; pointer-events: none; }
  .rc-chart-bars .grid i { display: block; border-top: 1px dashed var(--t-border); opacity: 0.25; }
  .rc-chart-grp { display: flex; align-items: flex-end; gap: 1.5px; flex: 1; height: 100%; position: relative; z-index: 1; padding: 0 1px; }
  .rc-chart-grp .bar { flex: 1; position: relative; min-height: 2px; box-shadow: inset 0 -2px 0 rgba(0,0,0,0.15); }
  .rc-chart-grp .b1 { background: var(--t-chart-1); }
  .rc-chart-grp .b2 { background: var(--t-chart-2); }
  .rc-chart-grp .b3 { background: var(--t-chart-3); }
  .rc-chart-grp .v { position: absolute; top: -11px; left: 50%; transform: translateX(-50%);
    font-size: 7px; font-weight: 800; color: var(--t-chart-1); white-space: nowrap; }
  .rc-chart-x { display: flex; justify-content: space-around; padding: 3px 2px 0 26px; font-size: 9px; font-weight: 700; color: var(--t-brand); }
  .rc-chart-x span { flex: 1; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .rc-chart-leg { display: flex; gap: 12px; padding: 6px 0 0 26px; font-size: 9px; font-weight: 600; white-space: nowrap; }
  .rc-chart-leg span { display: flex; align-items: center; gap: 5px; }
  .rc-chart-leg i { width: 11px; height: 11px; display: inline-block; box-shadow: inset 0 -2px 0 rgba(0,0,0,0.15); }

  .rc-remarks-h { font-family: "Roboto Slab", serif; font-weight: 700; font-size: 14px;
    color: var(--t-brand); letter-spacing: 0.16em; text-align: center; padding: 6px 0; border-bottom: 1px solid var(--t-border); }
  .rc-remarks { flex: 1; padding: 14px 14px 10px; background: var(--t-photo-bg); position: relative; }
  .rc-remarks::before { content: "\\201C"; position: absolute; top: 0; left: 8px;
    font-family: "Playfair Display", serif; font-size: 50px; line-height: 1;
    color: var(--t-brand); opacity: 0.3; font-weight: 700; }
  .rc-remarks-lines { display: flex; flex-direction: column; gap: 16px; padding: 18px 12px 0 18px; }
  .rc-remarks-lines i { display: block; border-bottom: 1px dotted var(--t-border); height: 0; }

  /* SIGNATURE */
  .rc-sign-h { background: var(--t-band-bg); color: var(--t-band-fg); border: 1px solid var(--t-border);
    text-align: center; padding: 6px 0; font-family: "Roboto Slab", serif; font-weight: 700;
    letter-spacing: 0.5em; font-size: 13px; text-transform: uppercase; }
  .rc-sign { display: grid; grid-template-columns: 1fr 1fr 1fr; border: 1px solid var(--t-border); border-top: 0; }
  .rc-sign .col { padding: 4px 10px 6px; text-align: center; border-right: 1px solid var(--t-line);
    font-size: 11px; font-weight: 700; letter-spacing: 0.04em; font-family: "Roboto Slab", serif;
    color: var(--t-brand); display: flex; flex-direction: column; justify-content: flex-end; min-height: 42px; }
  .rc-sign .col:last-child { border-right: 0; }

  @media print {
    html, body { background: #fff; }
    .rc { margin: 0; box-shadow: none; }
    @page { size: A4; margin: 0; }
  }
`;

const S = {
  card: {
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
    border: "none",
    marginBottom: 24,
  },
  cardHeader: {
    border: "1px solid #e5e7eb",
    background: "rgba(250, 251, 255, 1)",
    color: "#000",
    padding: "18px 24px",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  cardHeaderTitle: {
    color: "#000",
    fontWeight: 400,
    fontSize: 17,
    margin: 0,
  },
  cardHeaderIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "rgb(224,231,255)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6366f1",
    fontSize: 14,
  },
  cardBody: { padding: "22px 24px" },
  emptyState: { textAlign: "center", padding: "48px 20px", color: "#9ca3af" },
  emptyIcon: { fontSize: 48, marginBottom: 12, display: "block" },
  emptyText: { fontWeight: 600, fontSize: 15, color: "#6b7280", margin: 0 },
};

class PrintReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedYear: "",
      selectedClass: "",
      selectedStream: "",
      selectedExam: "",
      branchData: [],
      loading: false,
      downloadingExcel: false,
      students: [],
      total_count: 0,
      generated_count: 0,
      pending_count: 0,
      searched: false,
      selectSection: "",
      search: "",
      currentPage: 1,
      pageSize: 10,
    };
  }

  componentDidMount() {
    this.getBranches();
    const years = this.props.getYears || [];
    if (years.length > 0) {
      this.setState({ selectedYear: String(years[0].id) });
    }
    // Branch comes from the header (NavbarOne) — re-scope the branch list, which
    // is what the Class/Section options are built from.
    this.offBranchScope = onBranchScopeChange(() => this.getBranches());
  }

  componentWillUnmount() {
    if (this.offBranchScope) this.offBranchScope();
  }

  componentDidUpdate(prevProps) {
    const years = this.props.getYears || [];
    const prevYears = prevProps.getYears || [];
    if (prevYears.length === 0 && years.length > 0 && !this.state.selectedYear) {
      this.setState({ selectedYear: String(years[0].id) });
    }
  }

  getBranches = async () => {
    try {
      const result = await this.props.client.query({
        query: GET_BRANCHES,
        variables: { institution_id: parseInt(Cookies.get("institutionid")) },
        fetchPolicy: "network-only",
      });
      // The header's branch selection IS this page's branch filter, and it never
      // widens past the branches the user is authorized for.
      const branches = scopeBranches(result.data.getBranches || []);
      this.setState((prev) => {
        // A Section picked before the scope narrowed may no longer exist in it.
        const stillValid = branches.some((b) =>
          (b.sections || []).some((s) => String(s.id) === String(prev.selectSection))
        );
        return stillValid
          ? { branchData: branches }
          : { branchData: branches, selectSection: "", students: [], searched: false };
      });
    } catch {
      // ignore
    }
  };

  // The branch that owns the chosen Section. There is no Branch dropdown any more:
  // a section belongs to exactly one branch, so the branch_id the attendance
  // service expects is derived from the section rather than picked separately.
  getSelectedBranchId = () => {
    const branch = (this.state.branchData || []).find((b) =>
      (b.sections || []).some((s) => String(s.id) === String(this.state.selectSection))
    );
    return branch ? branch.id : null;
  };



  sortClassesByName = (classes) =>
    [...(classes || [])].sort((a, b) =>
      String(a.value ?? "").localeCompare(
        String(b.value ?? ""),
        undefined,
        { sensitivity: "base", numeric: true }
      )
    );

  sortSectionsByName = (sections) =>
    [...(sections || [])].sort((a, b) =>
      String(a.value ?? "").localeCompare(
        String(b.value ?? ""),
        undefined,
        { sensitivity: "base", numeric: true }
      )
    );

  handleSelectChange = (name, value) => {
    if (name === "selectedYear") {
      this.setState({ selectedYear: value, selectedClass: "", selectedStream: "", selectedExam: "", selectSection: "", students: [], searched: false });
    } else if (name === "selectedClass") {
      this.setState({ selectedClass: value, selectedStream: "", selectSection: "", students: [], searched: false });
    } else if (name === "selectedStream") {
      this.setState({ selectedStream: value, students: [], searched: false });
    } else if (name === "selectSection") {
      this.setState({ selectSection: value, students: [], searched: false });
    } else if (name === "selectedExam") {
      this.setState({ selectedExam: value }, () => {
        if (value) this.fetchStudents();
      });
    }
  };

  fetchStudents = async () => {
    const { selectedClass, selectedStream, selectedYear, selectedExam } = this.state;
    if (!selectedClass || !selectedYear || !selectedExam) return;

    this.setState({ loading: true, students: [], searched: false });
    try {
      const result = await this.props.client.query({
        query: GET_REPORT_CARD_STUDENTS,
        variables: {
          class_id: parseInt(selectedClass),
          stream_id: selectedStream ? parseInt(selectedStream) : null,
          academic_year_id: parseInt(selectedYear),
          exam_phase_id: parseInt(selectedExam),
          institute_id: parseInt(Cookies.get("institutionid")),
          page: 1,
          limit: 1000,
        },
        fetchPolicy: "network-only",
      });
      const data = result.data.getReportCardStudents;
      this.setState({
        students: data.students,
        total_count: data.total_count,
        generated_count: data.generated_count,
        pending_count: data.pending_count,
        searched: true,
        search: "",
        currentPage: 1,
      });
    } catch (err) {
      console.error("getReportCardStudents error:", err);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSearch = () => {
    this.fetchStudents();
  };

  getColumnStructure = () => {
    const { students } = this.state;
    if (!students.length || !students[0].subjects) return [];
    return students[0].subjects.map((sub) => ({
      subject_name: sub.subject_name,
      internals: sub.internals,
    }));
  };

  // ─── Report HTML builder ──────────────────────────────────────────────────

  buildReportPageHTML = (student) => {
    const instituteId = parseInt(Cookies.get("institutionid")) || 0;
    const tpl = getTemplate(instituteId);
    const LOGO = tpl.logo;

    const calcGrade = (total, max) => {
      if (!max) return '-';
      const p = (total / max) * 100;
      if (p >= 91) return 'A1';
      if (p >= 81) return 'A2';
      if (p >= 71) return 'B1';
      if (p >= 61) return 'B2';
      if (p >= 51) return 'C1';
      if (p >= 41) return 'C2';
      if (p >= 35) return 'D';
      return 'E';
    };

    const gradeToPoint = (g) =>
      ({ A1: 10, A2: 9, B1: 8, B2: 7, C1: 6, C2: 5, D: 4, E: 0 })[g] ?? '-';

    const subjects = (student.subjects || []).map((sub) => {
      const total = sub.subject_total || 0;
      const max = sub.subject_max || 0;
      const grade = calcGrade(total, max);
      return {
        name: sub.subject_name,
        total,
        max,
        grade,
        gradePoint: gradeToPoint(grade),
        classAvg: sub.class_avg || 0,
        highScore: sub.high_score || 0,
        internals: sub.internals || [],
      };
    });

    // Collect unique internal column names in order of first appearance
    const seenInt = new Set();
    const internalCols = [];
    subjects.forEach((s) => {
      s.internals.forEach((i) => {
        if (!seenInt.has(i.internal_name)) {
          seenInt.add(i.internal_name);
          internalCols.push(i.internal_name);
        }
      });
    });

    const overallGrade = calcGrade(student.total_earned, student.total_max);
    const cgpa = subjects.length
      ? (subjects.reduce((s, sub) => s + (parseFloat(sub.gradePoint) || 0), 0) / subjects.length).toFixed(2)
      : '-';

    const hasInternals = internalCols.length > 0;

    // Two-row thead when internals exist, single-row when not
    const theadHTML = hasInternals
      ? `<thead>
          <tr>
            <th rowspan="2" style="text-align:left;padding-left:16px;vertical-align:middle;">Subject</th>
            ${internalCols.map((n) => `<th style="font-size:10px;padding:5px 4px;white-space:nowrap;">${n}</th>`).join('')}
            <th rowspan="2" style="vertical-align:middle;">Total<br/>Marks</th>
            <th rowspan="2" style="vertical-align:middle;">Marks<br/>Obtained</th>
            <th rowspan="2" style="vertical-align:middle;">Overall<br/>Grade</th>
            <th rowspan="2" style="vertical-align:middle;">Grade<br/>Point</th>
          </tr>
          <tr>
            ${internalCols.map((n) => {
        const maxM = (student.subjects || []).reduce((m, sub) => {
          const f = (sub.internals || []).find((i) => i.internal_name === n);
          return f ? f.max_marks : m;
        }, 0);
        return `<th style="font-size:9px;padding:3px 4px;opacity:0.8;">(${maxM})</th>`;
      }).join('')}
          </tr>
        </thead>`
      : `<thead>
          <tr>
            <th style="width:26%;text-align:left;padding-left:16px">Subject</th>
            <th>Total Marks</th>
            <th>Marks Obtained</th>
            <th>Overall Grade</th>
            <th>Grade Point</th>
          </tr>
        </thead>`;

    // Subject rows — with dynamic internal cells
    const subjectRows = subjects.map((s) => {
      const intCells = internalCols.map((n) => {
        const found = s.internals.find((i) => i.internal_name === n);
        return `<td class="num">${found !== undefined ? (found.marks_obtained ?? '-') : '-'}</td>`;
      }).join('');
      return `<tr>
        <td class="name">${s.name}</td>
        ${intCells}
        <td class="num">${s.max}</td>
        <td class="num">${s.total}</td>
        <td class="grade">${s.grade}</td>
        <td class="num">${s.gradePoint}</td>
      </tr>`;
    }).join('');

    // Internal sum cells for TOTAL row
    const internalTotalCells = internalCols.map((n) => {
      const sum = subjects.reduce((acc, s) => {
        const found = s.internals.find((i) => i.internal_name === n);
        return acc + (found ? (found.marks_obtained || 0) : 0);
      }, 0);
      return `<td class="num">${sum}</td>`;
    }).join('');

    // Attendance
    const monthly = student.monthly_attendance || [];
    const pct = (n, t) => t > 0 ? Math.round((n / t) * 100) : 0;
    let attendanceHTML = '';

    if (monthly.length > 0) {
      const sumTotal = monthly.reduce((s, m) => s + m.total, 0);
      const sumPresent = monthly.reduce((s, m) => s + m.present, 0);
      const sumAbsent = monthly.reduce((s, m) => s + m.absent, 0);

      const monthThs = monthly.map((m) => `<th>${m.mon}</th>`).join('') + '<th>Total</th>';
      const totalCells = monthly.map((m) => `<td>${m.total}</td>`).join('') + `<td><b>${sumTotal}</b></td>`;
      const presCells = monthly.map((m) => `<td class="green">${m.present} &ndash; ${pct(m.present, m.total)}%</td>`).join('') + `<td class="green">${sumPresent}</td>`;
      const absCells = monthly.map((m) => `<td class="${m.absent > 0 ? 'red' : 'green'}">${m.absent} &ndash; ${pct(m.absent, m.total)}%</td>`).join('') + `<td class="${sumAbsent > 0 ? 'red' : 'green'}">${sumAbsent}</td>`;

      const first = monthly[0].mon;
      const last = monthly[monthly.length - 1].mon;
      const range = monthly.length > 1 ? `${first} &ndash; ${last}` : first;

      attendanceHTML = `
  <div class="rc-att-h">ATTENDANCE&nbsp;REPORT<span class="span">(${range})</span></div>
  <table class="rc-att">
    <thead><tr><th style="width:28%"></th>${monthThs}</tr></thead>
    <tbody>
      <tr><td class="lbl">Working Days&nbsp;(${sumTotal})</td>${totalCells}</tr>
      <tr><td class="lbl green">Present&nbsp;(${sumPresent})&nbsp;&middot;&nbsp;${pct(sumPresent, sumTotal)}%</td>${presCells}</tr>
      <tr><td class="lbl ${sumAbsent > 0 ? 'red' : 'green'}">Absent&nbsp;(${sumAbsent})&nbsp;&middot;&nbsp;${pct(sumAbsent, sumTotal)}%</td>${absCells}</tr>
    </tbody>
  </table>`;
    } else {
      const wd = student.working_days || 0;
      const dp = student.days_present || 0;
      const da = wd - dp;
      if (wd > 0) {
        attendanceHTML = `
  <div class="rc-att-h">ATTENDANCE&nbsp;REPORT</div>
  <table class="rc-att">
    <thead><tr><th style="width:28%"></th><th>Total</th></tr></thead>
    <tbody>
      <tr><td class="lbl">Working Days&nbsp;(${wd})</td><td>${wd}</td></tr>
      <tr><td class="lbl green">Present&nbsp;(${dp})&nbsp;&middot;&nbsp;${pct(dp, wd)}%</td><td class="green">${dp} &ndash; ${pct(dp, wd)}%</td></tr>
      <tr><td class="lbl ${da > 0 ? 'red' : 'green'}">Absent&nbsp;(${da})&nbsp;&middot;&nbsp;${pct(da, wd)}%</td><td class="${da > 0 ? 'red' : 'green'}">${da} &ndash; ${pct(da, wd)}%</td></tr>
    </tbody>
  </table>`;
      }
    }

    // Bar chart groups
    const barGroups = subjects.map((s) => {
      const sPct = Math.min(100, Math.max(1, (s.total / (s.max || 100)) * 100));
      const aPct = Math.min(100, Math.max(1, ((s.classAvg || 0) / (s.max || 100)) * 100));
      const hPct = Math.min(100, Math.max(1, ((s.highScore || 0) / (s.max || 100)) * 100));
      return `<div class="rc-chart-grp">
        <div class="bar b1" style="height:${sPct.toFixed(1)}%"><span class="v">${s.total}</span></div>
        <div class="bar b2" style="height:${aPct.toFixed(1)}%"></div>
        <div class="bar b3" style="height:${hPct.toFixed(1)}%"></div>
      </div>`;
    }).join('');

    const xLabels = subjects.map((s) => `<span>${s.name}</span>`).join('');

    const examTitle = (student.exam_phase || '').toUpperCase();
    const acYear = student.academic_year ? ` &nbsp;&middot;&nbsp; (${student.academic_year})` : '';

    const schoolsHTML = tpl.schools
      .map((s) => `<span class="b"><span class="nm">${s.name}</span><span class="area">${s.area}</span></span>`)
      .join('');

    const headerHTML = `<header class="rc-hdr">
      <div class="rc-crest">
        <span class="fallback">${tpl.fallbackLetter}</span>
        <img src="${LOGO}" alt="${tpl.brandName} crest"
          onload="this.previousElementSibling.style.display='none'"
          onerror="this.style.display='none'" />
      </div>
      <div class="rc-brand">
        <div class="nm">${tpl.brandName}</div>
        <div class="tag">${tpl.brandTag}</div>
        <div class="accent-line">
          <span class="l"></span>
          <span class="est">&#9733; &nbsp; &#9733; &nbsp; &#9733;</span>
          <span class="l" style="transform:scaleX(-1)"></span>
        </div>
      </div>
      <div class="rc-panel">${schoolsHTML}</div>
    </header>`;

    const stampStyle = tpl.stampSize
      ? `width:${tpl.stampSize}px;height:${tpl.stampSize}px;transform:rotate(-14deg);filter:contrast(1.05);object-fit:contain;display:block;`
      : '';

    return `<div class="rc">
  <div class="rc-stamp"><img src="${LOGO}" alt="" ${stampStyle ? `style="${stampStyle}"` : ''} /></div>

  <div class="rc-inner">
    ${headerHTML}

    <div class="rc-title">
      <span class="d">&#9670;</span>
      ${examTitle}${acYear} &nbsp;&middot;&nbsp; REPORT CARD
      <span class="d">&#9670;</span>
    </div>

    <div class="rc-bio">
      <table>
        <tbody>
          <tr><td class="lbl">Student Name</td><td>${(student.student_name || '').toUpperCase()}</td></tr>
          <tr><td class="lbl">Father&rsquo;s Name</td><td>N/A</td></tr>
          <tr><td class="lbl">Mother&rsquo;s Name</td><td>N/A</td></tr>
        </tbody>
      </table>
      <table>
        <tbody>
          <tr><td class="lbl">Class</td><td>${student.class || ''}</td></tr>
          <tr><td class="lbl">Roll No.</td><td>${student.admission_no || student.reg_id || ''}</td></tr>
          <tr><td class="lbl">Section</td><td></td></tr>
        </tbody>
      </table>
      <div class="photo">Photo</div>
    </div>

    <table class="rc-subj">
      ${theadHTML}
      <tbody>
        ${subjectRows}
        <tr class="tot">
          <td class="name">TOTAL</td>
          ${internalTotalCells}
          <td class="num">${student.total_max || '-'}</td>
          <td class="num">${student.total_earned || '-'}</td>
          <td class="grade">${overallGrade}</td>
          <td class="num"><span class="rc-cgpa">CGPA&nbsp;${cgpa}</span></td>
        </tr>
      </tbody>
    </table>

    <div class="rc-legend">
      <b>GRADES :</b>
      A1&ndash;10&thinsp;[91&ndash;100]&nbsp;&middot;&nbsp;
      A2&ndash;9&thinsp;[81&ndash;90]&nbsp;&middot;&nbsp;
      B1&ndash;8&thinsp;[71&ndash;80]&nbsp;&middot;&nbsp;
      B2&ndash;7&thinsp;[61&ndash;70]&nbsp;&middot;&nbsp;
      C1&ndash;6&thinsp;[51&ndash;60]&nbsp;&middot;&nbsp;
      C2&ndash;5&thinsp;[41&ndash;50]&nbsp;&middot;&nbsp;
      D&ndash;4&thinsp;[35&ndash;40]&nbsp;&middot;&nbsp;
      E&ndash;0&thinsp;[0&ndash;34]
    </div>

    ${attendanceHTML}

    <div class="rc-cr">
      <div>
        <div class="rc-chart">
          <div class="rc-chart-h">
            <span>Performance Overview</span>
            <span class="scale">Marks 0&ndash;100</span>
          </div>
          <div class="rc-chart-area">
            <div class="rc-chart-y">
              <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
            </div>
            <div class="rc-chart-bars">
              <div class="grid"><i></i><i></i><i></i><i></i><i></i></div>
              ${barGroups}
            </div>
          </div>
          <div class="rc-chart-x">${xLabels}</div>
          <div class="rc-chart-leg">
            <span><i style="background:var(--t-chart-1)"></i> Student</span>
            <span><i style="background:var(--t-chart-2)"></i> Class Avg</span>
            <span><i style="background:var(--t-chart-3)"></i> High Score</span>
          </div>
        </div>
      </div>
      <div>
        <div class="rc-remarks-h">REMARKS</div>
        <div class="rc-remarks">
          <div class="rc-remarks-lines">
            <i></i><i></i><i></i><i></i><i></i>
          </div>
        </div>
      </div>
    </div>

    <div class="rc-sign-h">S i g n a t u r e</div>
    <div class="rc-sign">
      <div class="col">Principal / HM</div>
      <div class="col">Class Teacher</div>
      <div class="col">Parent</div>
    </div>
  </div>
</div>`;
  };

  // Returns [{year, month(1-12), label:"May"}] — queries April of start year through April of end year
  // so we never miss attendance entered before the official academic year start (e.g. May data)
  getMonthsForExamPhase = (academicYear) => {
    const parts = (academicYear || "").split("-");
    const startYear = parseInt(parts[0]) || new Date().getFullYear();
    const endYear = startYear + 1;
    const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const months = [];
    // April & May of start year, then June–December, then Jan–April of end year
    for (let m = 4; m <= 12; m++) months.push({ year: startYear, month: m, label: SHORT[m - 1] });
    for (let m = 1; m <= 4; m++) months.push({ year: endYear, month: m, label: SHORT[m - 1] });
    return months;
  };

  printReportPDF = async (students) => {
    const { selectSection, selectedClass } = this.state;
    const selectBranch = this.getSelectedBranchId();
    const instituteId = parseInt(Cookies.get("institutionid")) || 0;
    const academicYear = students[0]?.academic_year || "";
    const months = this.getMonthsForExamPhase(academicYear);

    // Open popup early so browsers don't block it (must happen in same click handler tick)
    const win = window.open("", "_blank");
    if (!win) {
      alert("Allow popups for this site to print report cards.");
      return;
    }
    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Loading...</title></head><body><p style="font-family:Arial;padding:20px;font-size:14px;">Loading report, please wait...</p></body></html>`);

    // Fetch all months in parallel
    const IST_OFFSET = 19800; // DB stores timestamps as UTC - IST_OFFSET
    const monthResults = await Promise.all(
      months.map(async ({ year, month, label }) => {
        const fromDate = (Math.floor(Date.UTC(year, month - 1, 1) / 1000) - IST_OFFSET).toString();
        const toDate = (Math.floor(Date.UTC(year, month, 0, 23, 59, 59) / 1000) - IST_OFFSET).toString();
        try {
          const res = await this.props.client.query({
            query: GET_MONTHLY_ATTENDANCE,
            variables: {
              institute_id: instituteId,
              branch_id: parseInt(selectBranch) || 0,
              class_id: String(selectedClass),
              section_id: selectSection ? String(selectSection) : undefined,
              from_date: fromDate,
              to_date: toDate,
            },
            fetchPolicy: "network-only",
          });
          return { label, rows: res.data.getStudentAttendanceReportDetails || [] };
        } catch (_) {
          return { label, rows: [] };
        }
      }),
    );

    // Build monthlyMap: studentId → [{mon, total, present, absent}]
    const monthlyMap = new Map();
    monthResults.forEach(({ label, rows }) => {
      rows.forEach((row) => {
        if ((row.working_days || 0) === 0) return;
        const sid = row.id;
        if (!monthlyMap.has(sid)) monthlyMap.set(sid, []);
        monthlyMap.get(sid).push({
          mon: label,
          total: row.working_days || 0,
          present: row.present || 0,
          absent: row.absent || 0,
        });
      });
    });

    // Attach monthly_attendance and exam_months to each student before building HTML
    const enriched = students.map((s) => ({
      ...s,
      monthly_attendance: monthlyMap.get(s.student_id) || [],
      exam_months: months,
    }));

    const pages = enriched.map((s) => this.buildReportPageHTML(s)).join("\n");
    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<title>Report Cards</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,800;0,900;1,800;1,900&family=Roboto+Slab:wght@500;600;700&family=Inter+Tight:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
<style>${REPORT_CSS}</style>
</head>
<body>
${pages}
<script>
  window.addEventListener('load', function() {
    setTimeout(function() { window.print(); }, 600);
  });
</script>
</body>
</html>`;

    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  // ─── Excel download (CSV) ────────────────────────────────────────────────

  downloadExcel = () => {
    const { students } = this.state;
    if (!students.length) return;

    const columns = this.getColumnStructure();

    // Header row 1: subject names spanning internals + Total + GP columns
    const header1 = ["Student Name", "Reg ID"];
    // Header row 2: internal names, then Total, Grade Point per subject
    const header2 = ["", ""];

    columns.forEach((col) => {
      col.internals.forEach((int, i) => {
        header1.push(i === 0 ? col.subject_name : "");
        header2.push(`${int.internal_name}(${int.max_marks})`);
      });
    });
    header1.push("", "");
    header2.push("Grand Total", "CGPA");

    const dataRows = students.map((student) => {
      const row = [student.student_name, student.reg_id];
      let cgpa = "-";
      if ((student.subjects || []).length) {
        const gps = student.subjects.map((sub) => {
          const max = sub.subject_max || 0;
          return max > 0 ? Math.round((sub.subject_total / max) * 1000) / 100 : 0;
        });
        cgpa = (gps.reduce((s, g) => s + g, 0) / gps.length).toFixed(1);
      }

      columns.forEach((col) => {
        const sub = student.subjects.find((s) => s.subject_name === col.subject_name);
        col.internals.forEach((int) => {
          const internal = sub?.internals.find((i) => i.internal_name === int.internal_name);
          row.push(internal?.marks_obtained ?? "");
        });
      });
      row.push(
        `${student.total_earned ?? ""}/${student.total_max ?? ""}`,
        cgpa,
      );
      return row;
    });

    const csvContent = [header1, header2, ...dataRows]
      .map((row) =>
        row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ReportCards_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Renders ─────────────────────────────────────────────────────────────

  renderFilters() {
    const {
      selectedYear,
      selectedClass,
      selectedStream,
      selectedExam,
      loading,
      branchData,
      selectSection,
    } = this.state;
    const getClasses = this.props.getClasses || [];
    const getYears = this.props.getYears || [];
    const getExamPhase = this.props.getExamPhase || [];
    const getStreams = this.props.getStreams || [];

    // Class / Section options now span every branch the header has in scope,
    // instead of the one branch a local dropdown used to pick.
    const scopedSections = (branchData || []).flatMap((b) => b.sections || []);

    const classArr = this.sortClassesByName(
      [...new Set(scopedSections.map((s) => s.class_id))]
        .map((classId) => {
          const cls = getClasses.find((c) => c.id === classId);
          return cls ? { value: cls.id, label: cls.class, id: cls.id } : null;
        })
        .filter(Boolean)
    );

    const streamArr = selectedClass
      ? (getStreams || [])
          .filter((st) => {
            const classCsv = String(st.classes || "").split(",").map((v) => Number(v.trim())).filter(Boolean);
            return classCsv.includes(Number(selectedClass));
          })
          .map((st) => ({ value: st.id, label: st.stream, id: st.id }))
      : [];

    const sectionArr = this.sortSectionsByName(
      scopedSections
        .filter((s) => !selectedClass || String(s.class_id) === String(selectedClass))
        .map((s) => ({ value: s.id, label: s.section_name, id: s.id }))
    );

    return (
      <div style={S.card}>
        <div style={S.cardHeader}>
          <div style={S.cardHeaderIcon}>
            <i className="fas fa-book-open" />
          </div>
          <h5 style={S.cardHeaderTitle}>Subject Wise Mark Entry Configuration</h5>
        </div>

        <div style={S.cardBody}>
          <Row>
            <Col md={3}>
              <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                Academic Year
              </Form.Label>
              {(() => {
                const yearOptions = (getYears).map(year => ({
                  value: year.id, label: year.academic_year, id: year.id,
                }));
                return (
                  <SelectDropDown
                    name="selectedYear"
                    placeholderName="Select Year"
                    options={yearOptions}
                    stateData={selectedYear ? (yearOptions.find(o => String(o.value) === String(selectedYear)) || null) : null}
                    handleChange={this.handleSelectChange}
                    isClearable={true}
                  />
                );
              })()}
            </Col>

            {/* No Branch filter — the header's branch selection scopes this page. */}

            <Col md={3}>
              <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                Class
              </Form.Label>
              <SelectDropDown
                name="selectedClass"
                placeholderName="Select Class"
                options={classArr}
                stateData={selectedClass ? (classArr.find(o => String(o.value) === String(selectedClass)) || null) : null}
                handleChange={this.handleSelectChange}
                isClearable={true}
              />
            </Col>

            {streamArr.length > 0 && (
              <Col md={3}>
                <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                  Stream
                </Form.Label>
                <SelectDropDown
                  name="selectedStream"
                  placeholderName="Select Stream"
                  options={streamArr}
                  stateData={selectedStream ? (streamArr.find(o => String(o.value) === String(selectedStream)) || null) : null}
                  handleChange={this.handleSelectChange}
                  isClearable={true}
                />
              </Col>
            )}

            <Col md={3}>
              <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                Section
              </Form.Label>
              <SelectDropDown
                name="selectSection"
                placeholderName="Select Section"
                options={sectionArr}
                stateData={selectSection ? (sectionArr.find(o => String(o.value) === String(selectSection)) || null) : null}
                handleChange={this.handleSelectChange}
                isClearable={true}
                isDisabled={!selectedClass}
              />
            </Col>

            <Col md={3} className="mt-3">
              <Form.Label className="text-muted" style={{ fontSize: 13 }}>
                Exam Phase
              </Form.Label>
              {(() => {
                const examOptions = (
                  getExamPhase.filter(item => +item.academic_year_id === +selectedYear)
                ).map(ep => ({
                  value: ep.id, label: `${ep.exam_phase} - ${ep.exam_standard_name}`, id: ep.id,
                }));
                return (
                  <SelectDropDown
                    name="selectedExam"
                    placeholderName="Select Exam Phase"
                    options={examOptions}
                    stateData={selectedExam ? (examOptions.find(o => String(o.value) === String(selectedExam)) || null) : null}
                    handleChange={this.handleSelectChange}
                    isClearable={true}
                  />
                );
              })()}
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  renderTable() {
    const {
      students,
      loading,
      searched,
      total_count,
      generated_count,
      pending_count,
      downloadingExcel,
      search,
      currentPage,
      pageSize,
    } = this.state;

    const columns = this.getColumnStructure();

    // ── Search filter ──────────────────────────────────────────────────────
    const filteredStudents = search.trim()
      ? students.filter((s) =>
        (s.roll_no || "").toString().toLowerCase().includes(search.trim().toLowerCase()) ||
        (s.student_name || "").toLowerCase().includes(search.trim().toLowerCase()) ||
        (s.reg_id || "").toString().toLowerCase().includes(search.trim().toLowerCase()) ||
        (s.admission_no || "").toString().toLowerCase().includes(search.trim().toLowerCase()) ||
        (s.dob || "").toString().toLowerCase().includes(search.trim().toLowerCase()) ||
        (s.father_name || "").toString().toLowerCase().includes(search.trim().toLowerCase()) ||
        (s.mother_name || "").toString().toLowerCase().includes(search.trim().toLowerCase()) ||
        (s.father_mobile || "").toString().toLowerCase().includes(search.trim().toLowerCase())
      )
      : students;

    // ── Pagination ─────────────────────────────────────────────────────────
    const totalFiltered = filteredStudents.length;
    const totalPages = pageSize === "All" ? 1 : Math.ceil(totalFiltered / pageSize);
    const safePage = Math.min(currentPage, Math.max(totalPages, 1));
    const startIdx = pageSize === "All" ? 0 : (safePage - 1) * pageSize;
    const endIdx = pageSize === "All" ? totalFiltered : Math.min(startIdx + pageSize, totalFiltered);
    const pagedStudents = filteredStudents.slice(startIdx, endIdx);

    const hasData = !loading && students.length > 0;

    // ── Pagination button renderer ──────────────────────────────────────────
    const renderPagination = () => {
      if (!hasData) return null;
      const pageNumbers = [];
      const delta = 2;
      let left = Math.max(1, safePage - delta);
      let right = Math.min(totalPages, safePage + delta);
      if (safePage - delta < 1) right = Math.min(totalPages, right + (delta - safePage + 1));
      if (safePage + delta > totalPages) left = Math.max(1, left - (safePage + delta - totalPages));
      for (let i = left; i <= right; i++) pageNumbers.push(i);

      const btnBase = {
        border: "1px solid #dee2e6",
        borderRadius: 6,
        padding: "5px 12px",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        background: "#fff",
        color: "#374151",
        lineHeight: 1.4,
        minWidth: 36,
      };
      const btnActive = {
        ...btnBase,
        background: "#dee2e6",
        color: "#1f2937",
        borderColor: "#dee2e6",
      };
      const btnDisabled = { ...btnBase, opacity: 0.45, cursor: "not-allowed" };

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            padding: "12px 16px",
            borderTop: "1px solid #e5e7eb",
            background: "#fafafa",
          }}
        >
          {/* Left: page-size select + showing text */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <select
              value={pageSize}
              onChange={(e) => {
                const val = e.target.value === "All" ? "All" : parseInt(e.target.value);
                this.setState({ pageSize: val, currentPage: 1 });
              }}
              style={{
                background: "#f3e8ff",
                border: "1px solid #c084fc",
                color: "#6b21a8",
                borderRadius: 8,
                padding: "5px 10px",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {[10, 25, 50, 100, "All"].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>
              Showing {totalFiltered === 0 ? 0 : startIdx + 1} to {endIdx} of {totalFiltered} Results
            </span>
          </div>

          {/* Right: page buttons */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button
              style={safePage === 1 ? btnDisabled : btnBase}
              disabled={safePage === 1}
              onClick={() => this.setState({ currentPage: 1 })}
            >«</button>
            <button
              style={safePage === 1 ? btnDisabled : btnBase}
              disabled={safePage === 1}
              onClick={() => this.setState({ currentPage: safePage - 1 })}
            >‹</button>
            {left > 1 && <span style={{ padding: "0 4px", color: "#9ca3af" }}>…</span>}
            {pageNumbers.map((p) => (
              <button
                key={p}
                style={p === safePage ? btnActive : btnBase}
                onClick={() => this.setState({ currentPage: p })}
              >{p}</button>
            ))}
            {right < totalPages && <span style={{ padding: "0 4px", color: "#9ca3af" }}>…</span>}
            <button
              style={safePage === totalPages ? btnDisabled : btnBase}
              disabled={safePage === totalPages}
              onClick={() => this.setState({ currentPage: safePage + 1 })}
            >›</button>
            <button
              style={safePage === totalPages ? btnDisabled : btnBase}
              disabled={safePage === totalPages}
              onClick={() => this.setState({ currentPage: totalPages })}
            >»</button>
          </div>
        </div>
      );
    };

    return (
      <div style={S.page}>

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">

          {hasData && (
            <div>
              <h4 style={{ fontWeight: 700, color: "#1a237e", marginBottom: 4 }}>
                Report
              </h4>
              <span style={{ fontSize: 13, color: "#5f6b84" }}>
                Total: <b>{total_count}</b> &nbsp;·&nbsp; Generated:{" "}
                <b style={{ color: "#3350ea" }}>{generated_count}</b>{" "}
                &nbsp;·&nbsp; Pending:{" "}
                <b style={{ color: "#e67e00" }}>{pending_count}</b>
              </span>
            </div>
          )}

          {hasData && (
            <div style={{ display: "flex", gap: 10 }}>
              <Button
                onClick={this.downloadExcel}
                disabled={downloadingExcel}
                style={{
                  background: "#3350ea",
                  border: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: 10,
                  padding: "8px 18px",
                }}
              >
                {downloadingExcel ? (
                  <Spinner as="span" animation="border" size="sm" className="mr-2" />
                ) : (
                  "📊 Download Excel"
                )}
              </Button>
              <Button
                onClick={() => this.printReportPDF(students)}
                style={{
                  background: "#e53e3e",
                  border: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: 10,
                  padding: "8px 18px",
                }}
              >
                🖨 Print PDF (All)
              </Button>
            </div>
          )}
        </div>

        {/* Search toolbar */}
        {hasData && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 14,
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 340 }}>
              <i
                className="fas fa-search"
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9ca3af",
                  fontSize: 13,
                  pointerEvents: "none",
                }}
              />
              <input
                type="text"
                placeholder="Search by name, reg id..."
                value={search}
                onChange={(e) =>
                  this.setState({ search: e.target.value, currentPage: 1 })
                }
                style={{
                  width: "100%",
                  paddingLeft: 34,
                  paddingRight: 12,
                  paddingTop: 8,
                  paddingBottom: 8,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 13,
                  color: "#374151",
                  outline: "none",
                  background: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                }}
              />
            </div>
            {search && (
              <button
                onClick={() => this.setState({ search: "", currentPage: 1 })}
                style={{
                  background: "#f3e8ff",
                  border: "1px solid #c084fc",
                  color: "#6b21a8",
                  borderRadius: 8,
                  padding: "7px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted" style={{ fontSize: 14 }}>
              Fetching students...
            </p>
          </div>
        )}

        {/* No results */}
        {!loading && searched && students.length === 0 && (
          <div
            className="text-center py-5"
            style={{ color: "#8a95b0", fontWeight: 600 }}
          >
            No students found for the selected filters.
          </div>
        )}

        {/* Dynamic subject-internal table */}
        {hasData && (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", marginBottom: 8 }}>
            <div style={{ overflowX: "auto" }}>
              <Table bordered hover size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
                <thead>
                  <tr style={{ textAlign: "center" }}>
                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        whiteSpace: "nowrap",
                        borderColor: "#2d3e6e",
                      }}
                    >
                      S.No
                    </th>
                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        whiteSpace: "nowrap",
                        minWidth: 100,
                        borderColor: "#2d3e6e",
                      }}
                    >
                      Roll No
                    </th>

                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        whiteSpace: "nowrap",
                        minWidth: 100,
                        borderColor: "#2d3e6e",
                      }}
                    >
                      Adm No
                    </th>

                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        whiteSpace: "nowrap",
                        minWidth: 140,
                        borderColor: "#2d3e6e",
                      }}
                    >
                      Student Name
                    </th>

                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        whiteSpace: "nowrap",
                        minWidth: 100,
                        borderColor: "#2d3e6e",
                      }}
                    >
                      DOB
                    </th>

                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        whiteSpace: "nowrap",
                        minWidth: 120,
                        borderColor: "#2d3e6e",
                      }}
                    >
                      Father Name
                    </th>

                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        whiteSpace: "nowrap",
                        minWidth: 110,
                        borderColor: "#2d3e6e",
                      }}
                    >
                      Father Mobile
                    </th>

                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        whiteSpace: "nowrap",
                        minWidth: 120,
                        borderColor: "#2d3e6e",
                      }}
                    >
                      Mother Name
                    </th>

                    {columns.map((col) => (
                      <th
                        key={col.subject_name}
                        colSpan={col.internals.length}
                        style={{
                          fontWeight: 700,
                          textTransform: "uppercase",
                          background: "#1a237e",
                          color: "#fff",
                          letterSpacing: 0.8,
                          borderColor: "#2d3e6e",
                        }}
                      >
                        {col.subject_name}
                      </th>
                    ))}
                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        borderColor: "#2d3e6e",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Grand Total
                    </th>
                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        borderColor: "#2d3e6e",
                        whiteSpace: "nowrap",
                      }}
                    >
                      CGPA
                    </th>
                    <th
                      rowSpan={2}
                      style={{
                        verticalAlign: "middle",
                        fontWeight: 700,
                        background: "#1a237e",
                        color: "#fff",
                        borderColor: "#2d3e6e",
                      }}
                    >
                      PDF
                    </th>
                  </tr>
                  <tr style={{ textAlign: "center" }}>
                    {columns.map((col) =>
                      col.internals.map((int) => (
                        <th
                          key={`${col.subject_name}-${int.internal_name}`}
                          style={{
                            fontWeight: 600,
                            fontSize: 11,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                            background: "#2d3e6e",
                            color: "#cdd8ff",
                            borderColor: "#3a4f84",
                          }}
                        >
                          {int.internal_name}
                        </th>
                      )),
                    )}
                  </tr>
                </thead>

                <tbody>
                  {pagedStudents.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8 + columns.reduce((acc, c) => acc + c.internals.length, 0) + 3}
                        style={{ textAlign: "center", padding: "32px 20px", color: "#9ca3af", fontWeight: 600 }}
                      >
                        No matching students found.
                      </td>
                    </tr>
                  ) : (
                    pagedStudents.map((student, index) => {
                      const sNo = startIdx + index + 1;
                      const subs = student.subjects || [];
                      const gps = subs.map((sub) => {
                        const max = sub.subject_max || 0;
                        return max > 0 ? Math.round((sub.subject_total / max) * 1000) / 100 : 0;
                      });
                      const cgpa = gps.length
                        ? (gps.reduce((s, g) => s + g, 0) / gps.length).toFixed(1)
                        : "-";
                      const hasMarks = subs.some((sub) =>
                        (sub.internals || []).some((int) => (int.marks_obtained || 0) > 0)
                      );

                      return (

                        <tr key={student.student_id} style={{ textAlign: "center", verticalAlign: "middle" }}>
                          <td>{sNo}</td>

                          <td>{student.reg_id || student.roll_no || "-"}</td>
                          <td>{student.admission_no || "-"}</td>
                          <td style={{ textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>
                            {student.student_name}
                          </td>
                          <td>{student.dob || "-"}</td>
                          <td>{student.father_name || "-"}</td>
                          <td>{student.father_phone || "-"}</td>
                          <td>{student.mother_name || "-"}</td>

                          {columns.map((col) => {
                            const sub = student.subjects.find(
                              (s) => s.subject_name === col.subject_name,
                            );
                            return col.internals.map((int) => {
                              const internal = sub?.internals.find(
                                (i) => i.internal_name === int.internal_name,
                              );
                              return (
                                <td key={`${col.subject_name}-${int.internal_name}-${student.student_id}`}>
                                  {internal?.marks_obtained ?? "-"}
                                </td>
                              );
                            });
                          })}

                          <td style={{ fontWeight: 700, background: "#f0f4ff", whiteSpace: "nowrap" }}>
                            {student.total_earned != null ? `${student.total_earned}/${student.total_max}` : "-"}
                          </td>
                          <td style={{ fontWeight: 700, color: "#c8102e", whiteSpace: "nowrap" }}>
                            {cgpa}
                          </td>

                          <td>
                            {hasMarks && (
                              <Button
                                size="sm"
                                onClick={() => this.printReportPDF([student])}
                                style={{
                                  background: "#3350ea",
                                  border: "none",
                                  fontSize: 11,
                                  padding: "3px 10px",
                                  borderRadius: 6,
                                }}
                              >
                                PDF
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
            {renderPagination()}
          </div>
        )}
      </div>
    );
  }

  render() {
    return (
      <div style={{ padding: 25, borderRadius: 20 }}>
        {this.renderFilters()}
        {this.renderTable()}
      </div>
    );
  }
}
export default withApollo(PrintReport);